import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  categoryLabel,
  servicesApi,
  servicesQueryKeys,
  SERVICE_TYPES,
  serviceTypeLabel,
  VAT_CATEGORIES,
  vatLabel,
  type ClinicServiceItem,
  type ServiceType,
  type VatCategory,
} from "../../shared/api/services";
import {
  serviceCategoriesApi,
  serviceCategoryQueryKeys,
} from "../../shared/api/serviceCategories";
import { departmentsApi, departmentQueryKeys } from "../../shared/api/departments";
import { apiError } from "../../shared/api/client";
import { StatusPill } from "../../shared/ui/StatusPill";
import { DepartmentSelect } from "../../shared/ui/DepartmentSelect";
import {
  useRowSelection,
  BulkDeleteBar,
  runBulkDelete,
} from "../../shared/ui/bulkSelect";
import { vnd } from "../../shared/api/reports";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

/** Giá trị của bộ lọc Khoa cho "dịch vụ không thuộc khoa nào" — không trùng được với một uuid. */
export const NO_DEPARTMENT_FILTER = "__none__";

const NATIVE_SELECT_CLASS =
  "h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

/** T83 — Cài đặt dịch vụ. Danh sách dịch vụ + thao tác tạo/sửa/ngưng. */
export function ServicesSettingsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ClinicServiceItem | "new" | null>(
    null
  );
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | ServiceType>("");
  const [depFilter, setDepFilter] = useState("");
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkPending, setBulkPending] = useState(false);
  const sel = useRowSelection();

  const query = useQuery({
    queryKey: servicesQueryKeys.all(),
    queryFn: () => servicesApi.listAll(),
  });
  const catsQuery = useQuery({
    queryKey: serviceCategoryQueryKeys.all(),
    queryFn: () => serviceCategoriesApi.list(),
  });
  const cats = catsQuery.data ?? [];
  // listAll (kể cả khoa đã ngưng): một dịch vụ vẫn có thể đang gắn vào khoa đã ngưng, lọc theo
  // danh sách chỉ-đang-hoạt-động sẽ làm những dịch vụ đó không tìm ra được bằng bộ lọc này.
  const depsQuery = useQuery({
    queryKey: departmentQueryKeys.all(),
    queryFn: () => departmentsApi.listAll(),
  });
  const deps = depsQuery.data ?? [];
  const depName = (id: string | null) =>
    !id ? null : deps.find((d) => d.id === id)?.name ?? null;
  const catName = (code: string | null) =>
    !code ? "(chưa phân loại)" : cats.find((c) => c.code === code)?.name ?? categoryLabel(code);

  const grouped = useMemo(() => {
    const rows = (query.data ?? []).filter((s) => {
      if (catFilter && (s.category ?? "") !== catFilter) return false;
      if (typeFilter && s.serviceType !== typeFilter) return false;
      if (depFilter === NO_DEPARTMENT_FILTER) {
        if (s.departmentId) return false;
      } else if (depFilter && s.departmentId !== depFilter) return false;
      if (!q.trim()) return true;
      const needle = q.toLowerCase();
      return (
        s.name.toLowerCase().includes(needle) ||
        s.code.toLowerCase().includes(needle)
      );
    });
    const map = new Map<string, ClinicServiceItem[]>();
    for (const s of rows) {
      const key = s.category ?? "";
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(s);
    }
    return Array.from(map.entries()).sort((a, b) =>
      a[0].localeCompare(b[0], "vi")
    );
  }, [query.data, q, catFilter, typeFilter, depFilter]);

  function refresh() {
    qc.invalidateQueries({ queryKey: servicesQueryKeys.all() });
  }

  const disable = useMutation({
    mutationFn: (id: string) => servicesApi.disable(id),
    onSuccess: refresh,
  });
  const enable = useMutation({
    mutationFn: (id: string) => servicesApi.enable(id),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => servicesApi.remove(id),
    onSuccess: () => {
      setBulkError(null);
      refresh();
    },
    onError: (e: unknown) => setBulkError(apiError(e).detail),
  });
  async function bulkDelete() {
    setBulkPending(true);
    const { failed } = await runBulkDelete([...sel.selectedIds], servicesApi.remove);
    setBulkPending(false);
    sel.clear();
    refresh();
    setBulkError(
      failed.length ? `${failed.length} dịch vụ không xoá được (đang được dùng).` : null,
    );
  }

  return (
    <div>
      <PageHeader
        title="Cài đặt dịch vụ"
        description="Danh mục dịch vụ phòng khám — gom nhóm theo loại (Khám / Xét nghiệm / Chẩn đoán hình ảnh / …) để dễ tra cứu trong lúc đặt lịch và lập hoá đơn."
        actions={
          <Button type="button" onClick={() => setEditing("new")}>
            + Tạo dịch vụ
          </Button>
        }
      />

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo tên hoặc mã dịch vụ…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          className={`${NATIVE_SELECT_CLASS} sm:w-48`}
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          aria-label="Lọc theo nhóm dịch vụ"
        >
          <option value="">Tất cả nhóm</option>
          {cats.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          className={`${NATIVE_SELECT_CLASS} sm:w-48`}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as "" | ServiceType)}
          aria-label="Lọc theo loại dịch vụ"
        >
          <option value="">Tất cả loại</option>
          {SERVICE_TYPES.map((t) => (
            <option key={t.code} value={t.code}>
              {t.label}
            </option>
          ))}
        </select>
        <select
          className={`${NATIVE_SELECT_CLASS} sm:w-48`}
          value={depFilter}
          onChange={(e) => setDepFilter(e.target.value)}
          aria-label="Lọc theo khoa"
        >
          <option value="">Tất cả khoa</option>
          <option value={NO_DEPARTMENT_FILTER}>Không thuộc khoa</option>
          {deps.map((d) => (
            <option key={d.id} value={d.id}>
              {d.active ? d.name : `${d.name} (đã ngưng)`}
            </option>
          ))}
        </select>
      </div>

      {query.isError && (
        <p className="mt-3 text-sm text-destructive">
          Không tải được danh mục dịch vụ.
        </p>
      )}
      {query.isLoading && <p className="mt-3 text-sm text-muted-foreground">Đang tải…</p>}

      {bulkError && (
        <p className="mt-3 text-sm text-destructive">{bulkError}</p>
      )}
      {sel.count > 0 && (
        <div className="mt-4">
          <BulkDeleteBar
            count={sel.count}
            noun="dịch vụ"
            pending={bulkPending}
            onClear={sel.clear}
            onDelete={bulkDelete}
          />
        </div>
      )}

      {grouped.map(([cat, rows]) => {
        const allSel = rows.length > 0 && rows.every((s) => sel.selectedIds.has(s.id));
        const someSel = rows.some((s) => sel.selectedIds.has(s.id)) && !allSel;
        return (
        <div key={cat || "_none"} className="mt-4">
          <h2 className="mb-2 text-base font-semibold text-foreground">
            {catName(cat || null)}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {rows.length} dịch vụ
            </span>
          </h2>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-9 text-center">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-primary align-middle"
                        aria-label="Chọn tất cả trong nhóm"
                        checked={allSel}
                        ref={(el) => {
                          if (el) el.indeterminate = someSel;
                        }}
                        onChange={() => {
                          const next = new Set(sel.selectedIds);
                          if (allSel) rows.forEach((s) => next.delete(s.id));
                          else rows.forEach((s) => next.add(s.id));
                          sel.setSelectedIds(next);
                        }}
                      />
                    </TableHead>
                    <TableHead>Tên dịch vụ</TableHead>
                    <TableHead>Mã</TableHead>
                    <TableHead>Loại</TableHead>
                    <TableHead>Khoa</TableHead>
                    <TableHead className="text-right">Giá</TableHead>
                    <TableHead>Thuế</TableHead>
                    <TableHead className="text-right">Thời lượng</TableHead>
                    <TableHead>Trạng thái</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-primary align-middle"
                          aria-label="Chọn dòng"
                          checked={sel.selectedIds.has(s.id)}
                          onChange={() => sel.toggle(s.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <strong className="font-medium text-foreground">{s.name}</strong>
                        {s.description && (
                          <div className="text-xs text-muted-foreground">
                            {s.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.code}</TableCell>
                      <TableCell>{serviceTypeLabel(s.serviceType)}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {depName(s.departmentId) ?? "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {s.priceVnd != null ? vnd(s.priceVnd) : "—"}
                        {s.costPriceVnd != null && (
                          <div className="text-xs text-muted-foreground">
                            vốn {vnd(s.costPriceVnd)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{vatLabel(s.vatCategory)}</TableCell>
                      <TableCell className="text-right">
                        {s.durationMinutes != null
                          ? `${s.durationMinutes} phút`
                          : "—"}
                      </TableCell>
                      <TableCell>
                        {s.active ? (
                          <StatusPill tone="success">Đang dùng</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">Đã ngưng</StatusPill>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(s)}
                          >
                            Sửa
                          </Button>
                          {s.active ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => disable.mutate(s.id)}
                            >
                              Ngưng
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => enable.mutate(s.id)}
                            >
                              Dùng lại
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={remove.isPending}
                            title="Xoá vĩnh viễn dịch vụ (chỉ khi không còn tham chiếu)"
                            onClick={() => {
                              if (
                                confirm(
                                  `Xoá vĩnh viễn dịch vụ "${s.name}"? Không thể hoàn tác.`,
                                )
                              )
                                remove.mutate(s.id);
                            }}
                          >
                            Xoá
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
        );
      })}

      {!query.isLoading && grouped.length === 0 && (
        <Card className="mt-4">
          <CardContent className="p-6 text-sm text-muted-foreground">
            Chưa có dịch vụ nào. Bấm <em>+ Tạo dịch vụ</em> ở góc phải để bắt
            đầu.
          </CardContent>
        </Card>
      )}

      {editing && (
        <ServiceFormModal
          item={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ServiceFormModal({
  item,
  onClose,
  onSaved,
}: {
  item: ClinicServiceItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [code, setCode] = useState(item?.code ?? "");
  const [name, setName] = useState(item?.name ?? "");
  const [description, setDescription] = useState(item?.description ?? "");
  const [category, setCategory] = useState(item?.category ?? "");
  const formCats = useQuery({
    queryKey: serviceCategoryQueryKeys.all(),
    queryFn: () => serviceCategoriesApi.list(),
  });
  // Managed categories, plus the item's current one if it's a legacy code not
  // in the list (so editing never silently drops it).
  const categoryOptions: { code: string; name: string }[] = (() => {
    const list = (formCats.data ?? []).map((c) => ({ code: c.code, name: c.name }));
    if (item?.category && !list.some((c) => c.code === item.category)) {
      list.push({ code: item.category, name: item.category });
    }
    return list;
  })();
  const [priceVnd, setPriceVnd] = useState<number | "">(
    item?.priceVnd ?? ""
  );
  const [costPriceVnd, setCostPriceVnd] = useState<number | "">(
    item?.costPriceVnd ?? ""
  );
  const [vatCategory, setVatCategory] = useState<VatCategory | "">(
    item?.vatCategory ?? ""
  );
  const [serviceType, setServiceType] = useState<ServiceType>(
    item?.serviceType ?? "SERVICE"
  );
  const [sortOrder, setSortOrder] = useState<number | "">(
    item?.sortOrder ?? ""
  );
  const [durationMinutes, setDurationMinutes] = useState<number | "">(
    item?.durationMinutes ?? ""
  );
  const [departmentId, setDepartmentId] = useState<string | null>(
    item?.departmentId ?? null
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const cat = category || null;
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        category: cat || null,
        priceVnd: priceVnd === "" ? null : Number(priceVnd),
        costPriceVnd: costPriceVnd === "" ? null : Number(costPriceVnd),
        vatCategory: vatCategory === "" ? null : vatCategory,
        serviceType,
        sortOrder: sortOrder === "" ? null : Number(sortOrder),
        durationMinutes:
          durationMinutes === "" ? null : Number(durationMinutes),
        departmentId: departmentId,
      };
      if (item) {
        return servicesApi.update(item.id, payload);
      }
      return servicesApi.create({ code: code.trim(), ...payload });
    },
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            save.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {item ? "Sửa dịch vụ" : "Tạo dịch vụ mới"}
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4 grid gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="svc-code">Mã dịch vụ *</Label>
              <Input
                id="svc-code"
                required
                disabled={!!item}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="VD: KH-TQ"
              />
              {item && (
                <small className="text-xs text-muted-foreground">
                  Không thay đổi mã sau khi tạo.
                </small>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-name">Tên dịch vụ *</Label>
              <Input
                id="svc-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="VD: Khám tổng quát"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-category">Nhóm dịch vụ</Label>
              <select
                id="svc-category"
                className={NATIVE_SELECT_CLASS}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">— Chưa phân loại —</option>
                {categoryOptions.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Thêm/sửa nhóm ở Cài đặt → Danh mục → Nhóm dịch vụ.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-type">Loại dịch vụ</Label>
              <select
                id="svc-type"
                className={NATIVE_SELECT_CLASS}
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value as ServiceType)}
              >
                {SERVICE_TYPES.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
              <small className="text-xs text-muted-foreground">
                "Khám bệnh" là dịch vụ khám/tư vấn; còn lại là dịch vụ / thủ thuật.
              </small>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-department">Khoa</Label>
              <DepartmentSelect
                id="svc-department"
                value={departmentId}
                onChange={setDepartmentId}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="svc-price">Giá (VND)</Label>
                <Input
                  id="svc-price"
                  type="number"
                  min={0}
                  value={priceVnd}
                  onChange={(e) =>
                    setPriceVnd(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="150000"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-cost">Giá vốn (VND)</Label>
                <Input
                  id="svc-cost"
                  type="number"
                  min={0}
                  value={costPriceVnd}
                  onChange={(e) =>
                    setCostPriceVnd(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="svc-vat">Loại thuế</Label>
                <select
                  id="svc-vat"
                  className={NATIVE_SELECT_CLASS}
                  value={vatCategory}
                  onChange={(e) =>
                    setVatCategory(e.target.value as VatCategory | "")
                  }
                >
                  <option value="">— Không đặt —</option>
                  {VAT_CATEGORIES.map((v) => (
                    <option key={v.code} value={v.code}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-duration">Thời lượng (phút)</Label>
                <Input
                  id="svc-duration"
                  type="number"
                  min={0}
                  value={durationMinutes}
                  onChange={(e) =>
                    setDurationMinutes(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="15"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-sort">STT hiển thị</Label>
                <Input
                  id="svc-sort"
                  type="number"
                  min={0}
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(
                      e.target.value === "" ? "" : Number(e.target.value)
                    )
                  }
                  placeholder="100"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-desc">Mô tả</Label>
              <Textarea
                id="svc-desc"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <p className="mt-3 text-sm text-destructive">{error}</p>
          )}

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Đóng
            </Button>
            <Button
              type="submit"
              disabled={save.isPending || !name.trim() || (!item && !code.trim())}
            >
              {save.isPending ? "Đang lưu…" : item ? "Lưu thay đổi" : "Tạo dịch vụ"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
